/* MotherFerment cart drawer — vanilla JS port of production Alpine cart.
   Exposes window.MFCart with: open(), close(), add(item), addMultiple(items), refresh().
   Uses Shopify's section rendering API to live-update the drawer + header without reloads. */

(function () {
  'use strict';

  var DRAWER_SELECTOR = '#MfCartDrawer';
  var BODY_LOCK_CLASS = 'mf-cart-locked';
  var SECTION_ID_DEFAULT = 'mf-cart-drawer';

  var state = {
    open: false,
    loading: false,
    sectionId: null,
    boundEsc: false,
  };

  function getDrawer() { return document.querySelector(DRAWER_SELECTOR); }

  function getSectionId() {
    if (state.sectionId) return state.sectionId;
    var d = getDrawer();
    state.sectionId = (d && d.dataset.sectionId) || SECTION_ID_DEFAULT;
    return state.sectionId;
  }

  function setLoading(on) {
    state.loading = on;
    var d = getDrawer();
    if (!d) return;
    var l = d.querySelector('[data-mf-cart-loading]');
    if (l) l.classList.toggle('is-active', on);
  }

  function open() {
    var d = getDrawer();
    if (!d) return;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    state.open = true;
    document.body.classList.add(BODY_LOCK_CLASS);
    document.body.style.overflow = 'hidden';
    if (!state.boundEsc) {
      document.addEventListener('keydown', onKeydown);
      state.boundEsc = true;
    }
  }

  function close() {
    var d = getDrawer();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    state.open = false;
    document.body.classList.remove(BODY_LOCK_CLASS);
    document.body.style.overflow = '';
    if (state.boundEsc) {
      document.removeEventListener('keydown', onKeydown);
      state.boundEsc = false;
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') close();
  }

  function dispatchUpdate(cart) {
    document.dispatchEvent(new CustomEvent('mf-cart-updated', { detail: { cart: cart } }));
  }

  /* Sync the small cart-count badge in the header (and any other [data-mf-cart-count] el).
     Pulls latest count from /cart.js — cheap, doesn't require re-rendering header. */
  function syncBadge() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-mf-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        // Update legacy mf-cart link "(N)" text in header
        document.querySelectorAll('.mf-cart').forEach(function (el) {
          if (el.dataset.mfCartLinkBound !== '1') return; // only update ones we control
          el.textContent = 'Cart (' + cart.item_count + ')';
        });
        dispatchUpdate(cart);
        return cart;
      });
  }

  /* Re-render the cart drawer section (and optionally header) via Shopify's section API.
     Replaces #MfCartDrawer's inner content with the freshly rendered HTML. */
  function refresh() {
    setLoading(true);
    var sid = getSectionId();
    return fetch('/?sections=' + encodeURIComponent(sid))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[sid];
        if (!html) return;
        var wrap = document.createElement('div');
        wrap.innerHTML = html.trim();
        var fresh = wrap.querySelector(DRAWER_SELECTOR);
        if (!fresh) return;
        var current = getDrawer();
        if (!current) return;
        // Preserve open state during swap
        var wasOpen = current.classList.contains('is-open');
        current.innerHTML = fresh.innerHTML;
        if (wasOpen) current.classList.add('is-open');
        bindDrawerEvents();
      })
      .then(function () { return syncBadge(); })
      .catch(function (err) { console.error('[mf-cart] refresh failed', err); })
      .finally(function () { setLoading(false); });
  }

  function add(payload) {
    if (!payload || !payload.id) return Promise.reject(new Error('add: missing id'));
    setLoading(true);
    var body, headers;
    if (payload.selling_plan) {
      // selling_plan must go via URL-encoded form so Shopify reads it correctly
      var parts = ['id=' + payload.id, 'quantity=' + (payload.quantity || 1), 'selling_plan=' + payload.selling_plan];
      if (payload.properties) {
        Object.keys(payload.properties).forEach(function (k) {
          parts.push('properties[' + encodeURIComponent(k) + ']=' + encodeURIComponent(payload.properties[k]));
        });
      }
      body = parts.join('&');
      headers = { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' };
    } else {
      body = JSON.stringify({ items: [{ id: payload.id, quantity: payload.quantity || 1, properties: payload.properties || {} }] });
      headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    }
    return fetch('/cart/add.js', { method: 'POST', headers: headers, body: body })
      .then(function (r) { if (!r.ok) return r.json().then(function (e) { throw e; }); return r.json(); })
      .then(function () { return refresh(); })
      .then(function () { open(); })
      .catch(function (err) {
        setLoading(false);
        console.error('[mf-cart] add failed', err);
        alert((err && err.description) || 'We were unable to add that to your cart.');
      });
  }

  function addMultiple(items) {
    if (!items || !items.length) return Promise.resolve();
    if (items.length === 1) return add(items[0]);
    setLoading(true);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (r) { if (!r.ok) return r.json().then(function (e) { throw e; }); return r.json(); })
      .then(function () { return refresh(); })
      .then(function () { open(); })
      .catch(function (err) {
        setLoading(false);
        console.error('[mf-cart] addMultiple failed', err);
        alert((err && err.description) || 'We were unable to add those items.');
      });
  }

  function change(line, quantity) {
    setLoading(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refresh(); })
      .catch(function (err) {
        setLoading(false);
        console.error('[mf-cart] change failed', err);
      });
  }

  function remove(line) { return change(line, 0); }

  /* Wire up event delegation inside the drawer — re-bind after every refresh().
     We use direct binding rather than document-level delegation so the drawer
     can be cleanly destroyed if the section is reloaded. */
  function bindDrawerEvents() {
    var d = getDrawer();
    if (!d) return;
    // Close triggers
    d.querySelectorAll('[data-mf-cart-close]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        if (el.tagName === 'A') return; // let anchors navigate first
        e.preventDefault();
        close();
      });
    });
    // Empty-cart CTA (closes on click before navigation)
    d.querySelectorAll('a[data-mf-cart-close]').forEach(function (el) {
      el.addEventListener('click', function () { close(); });
    });
    // Qty +
    d.querySelectorAll('[data-mf-cart-qty-up]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var line = parseInt(btn.getAttribute('data-mf-cart-line'), 10);
        var item = btn.closest('[data-mf-cart-line]');
        var num = item ? item.querySelector('[data-mf-cart-qty-num]') : null;
        var current = num ? parseInt(num.textContent, 10) || 1 : 1;
        change(line, current + 1);
      });
    });
    // Qty -
    d.querySelectorAll('[data-mf-cart-qty-down]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var line = parseInt(btn.getAttribute('data-mf-cart-line'), 10);
        var item = btn.closest('[data-mf-cart-line]');
        var num = item ? item.querySelector('[data-mf-cart-qty-num]') : null;
        var current = num ? parseInt(num.textContent, 10) || 1 : 1;
        change(line, Math.max(0, current - 1));
      });
    });
    // Remove
    d.querySelectorAll('[data-mf-cart-remove]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var line = parseInt(btn.getAttribute('data-mf-cart-line'), 10);
        remove(line);
      });
    });
  }

  /* Intercept any product-form submits on the page so adds happen via AJAX → drawer
     instead of full-page redirect to /cart. Forms are detected by action="/cart/add"
     or by being inside a `form` tag with name='product'. */
  function bindProductForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var action = form.getAttribute('action') || '';
      var isAdd = /\/cart\/add/.test(action) || form.id && form.id.indexOf('ProductForm') === 0;
      if (!isAdd) return;
      e.preventDefault();
      var fd = new FormData(form);
      // Pull id, quantity, selling_plan, properties[*] out of the FormData
      var id = fd.get('id');
      var quantity = parseInt(fd.get('quantity') || '1', 10);
      var sellingPlan = fd.get('selling_plan');
      var properties = {};
      fd.forEach(function (val, key) {
        var m = key.match(/^properties\[(.+)\]$/);
        if (m) properties[m[1]] = val;
      });
      if (!id) return;
      var payload = { id: id, quantity: quantity, properties: properties };
      if (sellingPlan) payload.selling_plan = sellingPlan;
      add(payload);
    });
  }

  /* Mark cart links in the header so we know which ones to swap text on, and
     intercept their clicks to open the drawer instead of navigating to /cart. */
  function bindCartLinks() {
    document.querySelectorAll('a[href$="/cart"], a.mf-cart, a[data-mf-drawer-link]').forEach(function (a) {
      a.dataset.mfCartLinkBound = '1';
      a.addEventListener('click', function (e) {
        // Allow modifier-clicks to navigate normally (open in new tab, etc.)
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        open();
      });
    });
  }

  function init() {
    bindDrawerEvents();
    bindProductForms();
    bindCartLinks();
    // Initial badge sync (handles back-from-checkout state)
    syncBadge();
  }

  // Public API
  window.MFCart = {
    open: open,
    close: close,
    refresh: refresh,
    add: add,
    addMultiple: addMultiple,
    change: change,
    remove: remove,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
